import React, { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight, ArrowDown, ShieldCheck, Wrench, Gauge } from "lucide-react";

const headlineLines = [
  { text: "Din bil.", kind: "plain" },
  { text: "Vårt hantverk.", kind: "italic-blue" },
  { text: "Tydligt från", kind: "plain" },
  { text: "start till klart.", kind: "italic-red" },
];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: {
      duration: 1.05,
      ease: [0.2, 0.8, 0.2, 1],
      delay: 0.25 + i * 0.12,
    },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  // 3D tilt
  const rotX = useMotionValue(0);
  const rotY = useMotionValue(0);
  const srX = useSpring(rotX, { stiffness: 150, damping: 20 });
  const srY = useSpring(rotY, { stiffness: 150, damping: 20 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    rotY.set(px * 10);
    rotX.set(-py * 10);
  };
  const onLeave = () => {
    rotY.set(0);
    rotX.set(0);
  };

  return (
    <section
      id="top"
      ref={ref}
      className="relative pt-32 lg:pt-40 pb-24 lg:pb-32 overflow-hidden"
    >
      {/* Ambient lights */}
      <div className="absolute inset-0 radial-red pointer-events-none" />
      <div className="absolute inset-0 radial-blue pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-[380px] dot-grid opacity-50 pointer-events-none" />

      <div className="relative max-w-[1360px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
        {/* LEFT – text */}
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          {/* Status eyebrow */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D52027] opacity-70" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#D52027]" />
            </span>
            <span className="text-[0.72rem] font-semibold tracking-[0.2em] uppercase text-slate-700">
              Service • Reparation • Diagnostik
            </span>
          </motion.div>

          {/* Kinetic headline */}
          <h1 className="hero-text mt-7 text-[14vw] sm:text-[9vw] lg:text-[6.2vw] xl:text-[88px]">
            {headlineLines.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span
                  custom={i}
                  initial="hidden"
                  animate="show"
                  variants={lineVariants}
                  className={
                    line.kind === "italic-blue"
                      ? "italic text-[#1F3C88] font-normal"
                      : line.kind === "italic-red"
                      ? "italic text-[#D52027] font-normal"
                      : ""
                  }
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-8 max-w-xl text-[1.05rem] sm:text-[1.1rem] text-slate-600 leading-relaxed"
          >
            Service, reparation och felsökning med en enkel princip: du ska förstå
            vad bilen behöver, vad vi gör och vad det kostar innan vi sätter
            igång.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <a href="#boka" data-testid="hero-booking-cta" className="btn-primary">
              Boka verkstadsbesök
              <ArrowUpRight className="h-[18px] w-[18px]" />
            </a>
            <a href="#tjanster" data-testid="hero-services-cta" className="btn-ghost">
              Utforska tjänster
              <ArrowDown className="h-[18px] w-[18px]" />
            </a>
          </motion.div>

          {/* Trust mini strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.9 }}
            className="mt-12 grid grid-cols-3 max-w-lg gap-4"
          >
            {[
              { icon: ShieldCheck, label: "Ordentligt utfört", sub: "Fokus på rätt åtgärd" },
              { icon: Gauge, label: "Modern felsökning", sub: "När bilen inte känns rätt" },
              { icon: Wrench, label: "Tydlig prisbild", sub: "Innan arbetet börjar" },
            ].map((t, i) => (
              <div key={i} className="flex flex-col gap-1.5">
                <t.icon className="h-5 w-5 text-[#1F3C88]" strokeWidth={1.8} />
                <div className="text-[0.88rem] font-semibold text-slate-900 leading-tight">
                  {t.label}
                </div>
                <div className="text-[0.75rem] text-slate-500">{t.sub}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* RIGHT – 3D tilt image */}
        <div className="lg:col-span-5 relative">
          <motion.div
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            style={{ perspective: 1200 }}
            className="relative"
          >
            <motion.div
              style={{
                rotateX: srX,
                rotateY: srY,
                transformStyle: "preserve-3d",
              }}
              className="relative"
            >
              <motion.div
                style={{ y: imageY, scale: imageScale }}
                className="spotlight-frame clip-corner aspect-[4/5] w-full"
              >
                <img
                  src="https://images.unsplash.com/photo-1669418165084-35203cf006a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxjYXIlMjBtZWNoYW5pYyUyMHdvcmtzaG9wJTIwcmVwYWlyJTIwY2FyJTIwaGVhZGxpZ2h0JTIwYnJha2UlMjBkZXRhaWx8ZW58MHx8fHwxNzkxMTkyMzk2fDA&ixlib=rb-4.1.0&q=85"
                  alt="Närbild bilstrålkastare och kaross"
                  loading="eager"
                  className="w-full h-full object-cover"
                />
              </motion.div>

              {/* Floating status card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3, duration: 0.9 }}
                style={{ transform: "translateZ(60px)" }}
                className="absolute -left-4 lg:-left-10 bottom-10 bg-white rounded-2xl shadow-[0_24px_60px_-20px_rgba(15,23,42,0.3)] border border-slate-100 p-5 w-[260px]"
              >
                <div className="flex items-center justify-between">
                  <span className="ticker">Status • Verkstad</span>
                  <span className="flex h-2 w-2 rounded-full bg-emerald-500" />
                </div>
                <div className="mt-3 font-display text-[1.35rem] leading-tight text-slate-900">
                  Behöver bilen hjälp?
                </div>
                <div className="mt-1 text-sm text-slate-600">
                  Skicka en förfrågan direkt
                </div>
                <div className="mt-4 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: "72%" }}
                    transition={{ delay: 1.6, duration: 1.2, ease: "easeOut" }}
                    className="h-full bg-gradient-to-r from-[#1F3C88] to-[#D52027]"
                  />
                </div>
              </motion.div>

              {/* Floating circular badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.6, rotate: -30 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ delay: 1.5, duration: 1 }}
                style={{ transform: "translateZ(80px)" }}
                className="absolute -top-6 -right-4 lg:-right-10 h-[120px] w-[120px] rounded-full bg-[#1F3C88] text-white grid place-items-center shadow-lg"
              >
                <div className="text-center leading-tight px-3">
                  <div className="font-mono text-[0.6rem] tracking-[0.2em] uppercase opacity-80">
                    ZAM
                  </div>
                  <div className="font-display italic text-[1.4rem]">AUTO</div>
                  <div className="font-mono text-[0.6rem] tracking-[0.2em] uppercase opacity-80">
                    Verkstad
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="hidden lg:flex absolute left-10 bottom-8 items-center gap-3 text-slate-500"
      >
        <span className="ticker">Scrolla</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="h-10 w-[1px] bg-slate-300"
        />
      </motion.div>
    </section>
  );
}
